import { PurchaseType } from '~/types/licenses/licenses';
import { ContactType } from '~/types/person/contacts';
import { usePersonStore } from '~/stores/person';
import {
    personHasActiveLicense,
    personHasUnassignedSeat,
    resolveLicenseGate,
    spareSeatsFor,
    type LicenseGate,
} from '~/utils/license-access';

/**
 * The single licence gate for the loaded (possibly impersonated) person: one answer to "is this
 * person protected", and one route from that answer into the sales flow. Every surface that
 * offers protection asks here, so a lapsed seat, a spare unassigned seat and an employer-owned
 * seat get the same treatment wherever the member is standing.
 */
export const useLicenseAccess = () => {
    const personStore = usePersonStore();
    const { isUpgradeableLicense, openPurchaseModal, redirectEmployerPurchase } = useLicensePurchase();
    const { showWarning } = useGlobalToast();

    const hasActiveLicense = computed(() => personHasActiveLicense(personStore.person));
    const hasUnassignedSeat = computed(() => personHasUnassignedSeat(personStore.person));
    const gate = computed<LicenseGate>(() => resolveLicenseGate(personStore.person));

    /** The gate for a surface that also knows whether the plan reaches its feature. */
    const gateForFeature = (covered: boolean): LicenseGate =>
        resolveLicenseGate(personStore.person, covered);

    /**
     * Act on a gate. `allowed` does nothing; every other answer opens the one flow that resolves
     * it. Returns true when the member was sent somewhere, so callers read
     * `if (promptForGate(gate)) return;`. `feature` is the locked feature's gate key, carried to
     * checkout so its upgrade page leads with the plan that unlocks it.
     */
    const promptForGate = (gate: LicenseGate, feature?: string): boolean => {
        const person = personStore.person;
        if (!person || gate === 'allowed') return false;

        // A spare seat answers a feature only if its plan reaches that feature. Assigning an
        // Essentials seat to someone the Breach Monitoring button sent here leaves the feature
        // locked behind a seat they now hold — so with no spare that carries it, the answer is a
        // seat that does, and the spare stays for whoever it was bought for. The feature travels
        // into the assignment dialog too, which lists only the spares that carry it.
        if (gate === 'needs_gift' && feature && spareSeatsFor(person, feature).length === 0) {
            gate = 'needs_purchase';
        }

        if (gate === 'needs_gift') {
            openPurchaseModal({
                type: PurchaseType.GIFT,
                for: null,
                contact: {
                    type: ContactType.PERSON,
                    id: person.id,
                    name: `${person.first_name} ${person.last_name}`.trim(),
                },
                ...(feature ? { feature } : {}),
            });
            return true;
        }

        if (gate === 'needs_upgrade') {
            // An admin acting for the company that owns this license IS the owner's side of
            // "contact the license owner": the tier is lifted by buying the higher company license
            // and assigning it, in the business checkout. Asked BEFORE the refusal below, whose
            // premise — a live license the viewer does not control — is false for them, and which
            // is otherwise the dead end every locked-feature CTA leads an employer to, because
            // `promptForGate` is the seam all of them share (the sidebar license card, the exposure
            // sidebar, FeatureLockedModal, the account popover, the removal-request modal and every
            // control in Controls & Preferences).
            //
            // Placed in this branch only. The other gates reach openPurchaseModal, which asks the
            // same question itself — and asking it any earlier would send an employer with a spare
            // license to a checkout instead of assigning the license they already own.
            // The feature travels with it, and so does the license being upgraded: the business
            // checkout then leads with the company plan that carries the feature, or — for a bare
            // "Upgrade" that names no feature, which is what the sidebar card and the exposure
            // sidebar press — with the next company tier above the one on screen, instead of a
            // chooser that offers the tier the employee already holds.
            if (redirectEmployerPurchase(feature, person.license)) return true;

            // A live seat they do not control (employer- or family-owned) cannot enter checkout's
            // modify flow, and buying them a second seat would not lift the feature on the one they
            // actually use — so say who has to act instead.
            if (!isUpgradeableLicense(person.license)) {
                showWarning(
                    'License Access Required',
                    'You do not control this license. Please contact the license owner to upgrade your plan.',
                );
                return true;
            }
            openPurchaseModal({
                type: PurchaseType.UPGRADE,
                for: person.id,
                license: person.license ?? undefined,
                feature,
            });
            return true;
        }

        openPurchaseModal({ type: PurchaseType.PURCHASE, for: person.id, feature });
        return true;
    };

    /**
     * Guard for an action that requires protection: true when the member may proceed, otherwise
     * the sales flow is open and the caller stops.
     */
    const ensureAccess = (covered = true): boolean => {
        const answer = gateForFeature(covered);
        if (answer === 'allowed') return true;
        promptForGate(answer);
        return false;
    };

    return {
        hasActiveLicense,
        hasUnassignedSeat,
        gate,
        gateForFeature,
        promptForGate,
        ensureAccess,
    };
};
